<template>
  <div class="container">
    <div class="row">
      <div class="col">
        <h3 class="text-center">
          <span style="padding: 0 12px;border-left:1px solid;border-right:1px solid;">{{ $t('event.list.review.title') }}</span>
        </h3>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <b-breadcrumb style="background-color: transparent;">
          <b-breadcrumb-item :to="{ name: 'index' }">{{ $t('index.title') }}</b-breadcrumb-item>
          <b-breadcrumb-item>{{ $t('event.title') }}</b-breadcrumb-item>
          <b-breadcrumb-item :to="{ name: 'event-review' }">{{ $t('event.list.review.title') }}</b-breadcrumb-item>
        </b-breadcrumb>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12">
        <b-nav pills fill>
          <b-nav-item :to="{ name: 'hwadzan-garden-intro' }">{{ $t('hwadzan-garden.title') }}</b-nav-item>
          <b-nav-item :to="{ name: 'latest-events' }">{{ $t('event.list.latest.title') }}</b-nav-item>
          <b-nav-item :to="{ name: 'event-review' }" active>{{ $t('event.list.review.title') }}</b-nav-item>
        </b-nav>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12 col-md-8 mb-2 mb-md-0">
        <b-form-select v-model="year">
          <b-form-select-option :value="year" v-for="year in years">{{ $t('event.list.year-selector.option.label', { year: year }) }}</b-form-select-option>
        </b-form-select>
      </div>
      <div class="col-12 col-md-4 d-flex align-items-center justify-content-md-end">
        <b-button variant="outline-primary" :to="{ name: 'classified-series', query: { category_no: 'C039' } }" target="_blank">
          {{ $t('event.list.button.browse-video-or-audio.label') }}
        </b-button>
      </div>
    </div>
    <b-overlay :show="is_loading" rounded="sm" opacity="0.6">
      <div class="row">
        <div class="col">
          <b-table
            fixed
            striped
            stacked="lg"
            show-empty
            :empty-text="$t('event.list.review.message.no-event-this-year', { year: year })"
            :fields="fields"
            :items="events"
            :sort-by.sync="ordering"
          >
            <template #cell(start_date)="data">
              {{ data.value.slice(0, 10) }}
              ({{ days_of_week[dayjs(data.value.slice(0, 10)).day()] }})
              <span style="color:red" v-if="data.item.start_date_note">
                {{ data.item.start_date_note }}
              </span>
            </template>
            <template #cell(end_date)="data">
              {{ data.value.slice(0, 10) }}
              ({{ days_of_week[dayjs(data.value.slice(0, 10)).day()] }})
              <span style="color:red" v-if="data.item.end_date_note">
                {{ data.item.end_date_note }}
              </span>
            </template>
            <template #cell(sidelight)="data">
              <b-button block variant="outline-primary" @click="showSidelightInfo(data.value)" v-if="data.value">
                {{ $t('event.list.button.sidelight.label') }}
              </b-button>
              <span v-else>{{ $t('event.list.message.none') }}</span>
            </template>
          </b-table>
        </div>
      </div>
      <template #overlay>
        <div class="text-center">
          <b-icon icon="arrow-clockwise" font-scale="5" animation="spin-pulse"></b-icon>
        </div>
      </template>
    </b-overlay>
    <info-modal id="sidelight-info-modal" :title="$t('event.list.review.modal.sidelight.title')">
      <template #info>
        <sidelight-info :sidelight="sidelight"></sidelight-info>
      </template>
    </info-modal>
  </div>
</template>
<script>
import Vue from 'vue'

import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

import { BreadcrumbPlugin } from 'bootstrap-vue'
import { ButtonGroupPlugin } from 'bootstrap-vue'
import { ButtonPlugin } from 'bootstrap-vue'
import { ButtonToolbarPlugin } from 'bootstrap-vue'
import { FormSelectPlugin } from 'bootstrap-vue'
import { IconsPlugin } from 'bootstrap-vue'
import { OverlayPlugin } from 'bootstrap-vue'
import { TablePlugin } from 'bootstrap-vue'

import SidelightInfo from '@/components/info/SidelightInfo.vue'
import InfoModal from '@/components/modals/InfoModal.vue'
import i18n from '@/plugins/i18n.js'
import EventApiService from '@/services/budaedu-api-services/event-api-service.js'
import SidelightApiService from '@/services/budaedu-api-services/sidelight-api-service.js'

Vue.use(BreadcrumbPlugin)
Vue.use(ButtonGroupPlugin)
Vue.use(ButtonPlugin)
Vue.use(ButtonToolbarPlugin)
Vue.use(FormSelectPlugin)
Vue.use(IconsPlugin)
Vue.use(OverlayPlugin)
Vue.use(TablePlugin)

Vue.use(EventApiService)
Vue.use(SidelightApiService)

export default {
  i18n,
  name: 'event-review',
  data() {
    return {
      is_loading: false,
      year: dayjs().year(),
      years: [],
      events: [],
      event: null,
      sidelight: null
    }
  },
  computed: {
    days_of_week() {
      return [
        this.$t('days-of-week.sunday.abbr'),
        this.$t('days-of-week.monday.abbr'),
        this.$t('days-of-week.tuesday.abbr'),
        this.$t('days-of-week.wednesday.abbr'),
        this.$t('days-of-week.thursday.abbr'),
        this.$t('days-of-week.friday.abbr'),
        this.$t('days-of-week.saturday.abbr')
      ]
    },
    fields() {
      return [
        { key: 'start_date', label: this.$t('event.list.table.header.start-date') },
        { key: 'end_date', label: this.$t('event.list.table.header.end-date') },
        { key: 'name', label: this.$t('event.list.table.header.name') },
        { key: 'organizer.name', label: this.$t('event.list.table.header.organizer') },
        { key: 'leader', label: this.$t('event.list.table.header.leader') },
        { key: 'sidelight', label: this.$t('event.list.table.header.sidelight') },
      ]
    }
  },
  watch: {
    year(new_value, old_value) {
      this.setEvents()
    }
  },
  methods: {
    dayjs(param) {
      return dayjs(param)
    },
    async setYears() {
      this.years = await this.$eventApiService.getListOfEventYear()
    },
    async setEvents() {
      this.is_loading = true
      this.events = []
      try {
        const items = await this.$eventApiService.query(
          `filter[of_year]=${this.year}&filter[end_date][lt]=${dayjs().format('YYYY-MM-DD')}&include=organizer,detail.attachments&order=start_date,asc`
        )

        const promises = items.data.map(async (item) => {
          item.sidelight = await this.$sidelightApiService.getByRelatedResource('event', item.id)
          if (item.sidelight) {
            item.sidelight.photo_compressed_files = await this.getSidelightPhotoCompressedFiles(item.sidelight.id)
            item.sidelight.photos = await this.getSidelightPhotos(item.sidelight.id)
            item.sidelight.videos = await this.getSidelightVideos(item.sidelight.id)
          }
        })
        await Promise.all(promises)

        this.events = items.data
      } finally {
        this.is_loading = false
      }
    },
    showSidelightInfo(sidelight) {
      this.sidelight = sidelight
      this.$bvModal.show('sidelight-info-modal')
    },
    async getSidelightPhotoCompressedFiles(sidelight_id) { 
      const { data } = await this.$sidelightApiService.fetchPhotoCompressedFiles(sidelight_id)
      return data
    },
    async getSidelightPhotos(sidelight_id) { 
      const { data } = await this.$sidelightApiService.fetchPhotos(sidelight_id)
      return data
    },
    async getSidelightVideos(sidelight_id) { 
      const { data } = await this.$sidelightApiService.fetchVideos(sidelight_id);
      const videos = data.map(item => {
        item.name = item.description
        item.thumbnail = [
          {
            src: 'https://www2.budaedu.org/book-img/CHCoverS/CHtemp.jpg',
            style: 'width:15em;height:3em;'
          }
        ]
        return item;
      }).sort((a, b) => a.name.localeCompare(b.name))
      return videos
    }
  },
  async created() {
    //alert(`Vue version : ${Vue.version}`);
    await this.setEvents()
    await this.setYears()
  },
  components: {
    InfoModal,
    SidelightInfo
  },
  metaInfo() {
    return {
      title: this.$t('event.list.review.title'),
      meta: [{
        vmid: 'description',
        name: 'description',
        content: ''
      }]
    }
  }
};
</script>